import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BookSlotModal from './BookSlotModal';
import { getMeetings, bookSlot } from '../services/api';

/**
 * ApplyToSpeakModal — two-step flow: pick an open slot, then book it.
 *
 * Props:
 *   bookedMeetingIds {string[]} — meetings where the member already has a slot
 *   onClose          {fn}       — called on cancel
 *   onBooked         {fn}       — called after successful booking
 */
export default function ApplyToSpeakModal({ bookedMeetingIds, onClose, onBooked }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'book'
  const [bookableMeetings, setBookableMeetings] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (step !== 'pick') return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, step]);

  useEffect(() => {
    getMeetings('upcoming', 10)
      .then((res) => {
        const meetings = res?.meetings || [];
        const today = new Date().toISOString().split('T')[0];
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() + 14);
        const cutoffStr = cutoff.toISOString().split('T')[0];

        const filtered = meetings.filter(m =>
          m.status === 'scheduled' &&
          m.meeting_date >= today &&
          m.meeting_date <= cutoffStr &&
          !bookedMeetingIds.includes(m.meeting_id) &&
          m.slots?.some(s => s.status === 'open')
        );
        setBookableMeetings(filtered);
      })
      .catch(() => setError('Could not load upcoming meetings.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSlotSelect = (slot, meeting) => {
    setSelectedSlot({ ...slot, meeting_date: meeting.meeting_date, meeting_title: meeting.title });
    setStep('book');
  };

  const handleConfirmBooking = async (presentation_title, synopsis) => {
    await bookSlot(selectedSlot.slot_id, { presentation_title, synopsis });
    onBooked();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (step === 'book' && selectedSlot) {
    return (
      <BookSlotModal
        meetingDate={selectedSlot.meeting_date}
        onConfirm={handleConfirmBooking}
        onClose={() => setStep('pick')}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
      >
        <div>
          <h3 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Get on stage
          </h3>
          <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-faint)' }}>
            Open slots, next 14 days
          </p>
        </div>

        {loading ? (
          <div className="py-8 text-center">
            <p className="text-sm" style={{ color: 'var(--text-faint)' }}>Loading…</p>
          </div>
        ) : error ? (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--color-danger)' }}>⚠ {error}</p>
        ) : bookableMeetings.length === 0 ? (
          <div className="py-8 text-center space-y-3">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              No open slots in the next 14 days.
            </p>
            <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
              Ask your admin to open a slot, or check the schedule for upcoming meetings.
            </p>
            <Link
              to="/calendar"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-sm font-semibold"
              style={{ color: 'var(--accent)' }}
            >
              View schedule →
            </Link>
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {bookableMeetings.map(meeting =>
              meeting.slots
                .filter(s => s.status === 'open')
                .map(slot => (
                  <button
                    key={slot.slot_id}
                    onClick={() => handleSlotSelect(slot, meeting)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-colors"
                    style={{
                      background: 'var(--accent-dim)',
                      border: '1px solid var(--border-accent)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-accent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-accent)'}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold break-words" style={{ color: 'var(--text-primary)' }}>
                        Slot {slot.slot_number} — {formatDate(meeting.meeting_date)}
                      </p>
                      <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-faint)' }}>
                        {meeting.title || 'Weekly Meeting'}
                      </p>
                    </div>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                      style={{ background: 'var(--color-success-dim)', color: 'var(--color-success)', border: '1px solid var(--color-success-border)' }}
                    >
                      Open
                    </span>
                  </button>
                ))
            )}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="pill-tab text-sm"
            style={{ color: 'var(--text-muted)' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
